import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';

const HOST = environment.serverUrl;
const API_URL = HOST + '/ocpp/session-analyses';

export type AnalysisStatus = 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';
export type AnalysisTrigger = 'MANUAL' | 'SCHEDULED';
export type IssueSeverity = 'INFO' | 'WARNING' | 'DANGER';
export type IssueScope = 'CHARGE_POINT' | 'NAMESPACE' | 'VENDOR' | 'MODEL' | 'FIRMWARE';

export interface OcppSessionAnalysisParameters {
  windowStart: string;
  windowEnd: string;
  namespaces?: string[];
  chargePointIds?: string[];
  vendors?: string[];
  models?: string[];
  firmwareVersions?: string[];
}

export interface SessionStabilityStats {
  chargePointCount: number;
  sessionCount: number;
  closedSessionCount: number;
  openSessionCount: number;
  veryShortSessionCount: number;
  shortSessionCount: number;
  burstCount: number;
  overlapCount: number;
  invalidSessionCount: number;
  unstableChargePointCount: number;
  veryShortRate: number;
  shortRate: number;
  burstRate: number;
  overlapRate: number;
  uptimeRatio: number;
  medianDurationSeconds: number;
  p95DurationSeconds: number;
  worstChargePointId?: string;
}

export interface SessionGroupStats {
  key: string;
  label: string;
  scope: IssueScope;
  chargePointId?: string;
  namespace?: string;
  vendor?: string;
  model?: string;
  firmwareVersion?: string;
  stats: SessionStabilityStats;
}

export interface AnalysisIssue {
  code: string;
  severity: IssueSeverity;
  scope: IssueScope;
  subject: string;
  message: string;
  measures?: Record<string, number>;
}

export interface OcppSessionAnalysisResult {
  version: number;
  summary: SessionStabilityStats;
  byChargePoint: SessionGroupStats[];
  byNamespace: SessionGroupStats[];
  byVendor: SessionGroupStats[];
  byModel: SessionGroupStats[];
  byFirmware: SessionGroupStats[];
  issues: AnalysisIssue[];
}

export interface OcppSessionAnalysis {
  id: string;
  status: AnalysisStatus;
  trigger: AnalysisTrigger;
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  progressDone: number;
  progressTotal: number;
  parameters: OcppSessionAnalysisParameters;
  result?: OcppSessionAnalysisResult;
  errorMessage?: string;
}

@Injectable({ providedIn: 'root' })
export class OcppSessionAnalysisApiService {
  constructor(private http: HttpClient) {}

  public list(): Observable<OcppSessionAnalysis[]> {
    return this.http.get<OcppSessionAnalysis[]>(API_URL);
  }

  public get(id: string): Observable<OcppSessionAnalysis> {
    return this.http.get<OcppSessionAnalysis>(`${API_URL}/${id}`);
  }

  public create(parameters: OcppSessionAnalysisParameters): Observable<OcppSessionAnalysis> {
    return this.http.post<OcppSessionAnalysis>(API_URL, parameters);
  }

  public delete(id: string): Observable<void> {
    return this.http.delete<void>(`${API_URL}/${id}`);
  }
}
